import React, { useEffect, useMemo, useState } from "react";
import Layout from "../components/Layout";
import { useNavigate, useLocation, Link } from "react-router-dom";
import apiFetch from "../utils/apiFetch";
import normalize, { fuzzyIncludes } from "../utils/normalize";
import SchemeModal from "../components/schemeModal/SchemeModal";

const DIRECTUS_URL =
  process.env.REACT_APP_DIRECTUS_URL || "https://map.uzbekistonmet.uz";
const ACCIDENTS_PAGE_SIZE = 30;

const formatDate = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);

  return new Intl.DateTimeFormat("ru-RU", {
    timeZone: "Asia/Tashkent",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(d);
};

const formatDateTime = (value) => (value ? formatDate(value) : "");

const isNonEmpty = (v) =>
  v !== null && v !== undefined && String(v).trim() !== "";

const maxDate = (dates) => {
  const valid = (dates || [])
    .filter(Boolean)
    .map((d) => new Date(d).getTime())
    .filter((t) => !Number.isNaN(t));

  if (!valid.length) return null;
  return new Date(Math.max(...valid)).toISOString();
};

// -------------------- Updates normalize --------------------

const normalizeUpdate = (u, type) => {
  const authorName = u.author_user
    ? `${u.author_user.lastname || ""} ${u.author_user.name || ""}`.trim()
    : "";

  const files = Array.isArray(u.files) ? u.files : [];
  const photos = files
    .filter((f) => f?.directus_files_id)
    .map((f) => ({
      id: f.directus_files_id,
      url: `${DIRECTUS_URL}/assets/${f.directus_files_id}`,
    }));

  return {
    id: u.id,
    kind: "update",
    dateCreated:
    u.date_created ||
    u.created_at ||
    u.createdAt ||
    u.event_date ||
    u.date ||
    u.repair_date ||
    u.repair_time ||
    null,
    authorName,

    eventDate:
      type === "network"
        ? u.date || u.event_date || null
        : u.event_date || u.date || null,

    repairDate: u.repair_date || u.repair_time || null,

    topic: u.topic || "",
    protectionType: u.protection_type || "",
    recorderReading: u.recorder_reading || "",
    cause: u.cause || "",
    measures: u.measures || "",
    result: u.result || "",
    employeeDetails: u.employee_details || "",
    photos,
  };
};

// -------------------- Timeline (single "current view" + histories per field) --------------------

const buildAccidentTimeline = (acc) => {
  const baseEntry = {
    id: "base",
    kind: "base",
    dateCreated: acc.dateCreated,
    authorName: acc.authorName,
    eventDate: acc.eventDate,
    repairDate: acc.repairDate,
    topic: acc.topic,
    protectionType: acc.protectionType,
    recorderReading: acc.recorderReading,
    cause: acc.cause,
    measures: acc.measures,
    result: acc.result,
    employeeDetails: acc.employeeDetails,
    photos: acc.photos || [],
  };

const parseTs = (value) => {
  if (!value) return null;
  const t = new Date(value).getTime();
  return Number.isNaN(t) ? null : t;
};

const getEntryTs = (e) => {
  const t =
    parseTs(e?.dateCreated) ??
    parseTs(e?.eventDate) ??
    parseTs(e?.repairDate);

  // ВАЖНО:
  // - base без даты можно оставить 0 (самое раннее)
  // - update без даты нельзя делать 0, иначе он станет "самым старым" и base перетрёт его
  if (t == null) return e.kind === "update" ? Number.POSITIVE_INFINITY : 0;

  return t;
};

const entriesAsc = [baseEntry, ...(acc.updates || [])]
  .slice()
  .sort((a, b) => {
    const ta = getEntryTs(a);
    const tb = getEntryTs(b);
    if (ta !== tb) return ta - tb;

    // tie-break: base раньше updates
    if (a.kind !== b.kind) return a.kind === "base" ? -1 : 1;

    return 0;
  });

  const effective = { ...acc, photos: [] };

  const photoMap = new Map();
  const applyPhotos = (arr) => {
    (arr || []).forEach((p) => {
      if (p?.id && p?.url && !photoMap.has(p.id)) photoMap.set(p.id, p);
    });
  };

  for (const e of entriesAsc) {
    if (isNonEmpty(e.authorName)) effective.authorName = e.authorName;
    if (isNonEmpty(e.eventDate)) effective.eventDate = e.eventDate;
    if (isNonEmpty(e.repairDate)) effective.repairDate = e.repairDate;

    if (isNonEmpty(e.topic)) effective.topic = e.topic;
    if (isNonEmpty(e.protectionType)) effective.protectionType = e.protectionType;
    if (isNonEmpty(e.recorderReading)) effective.recorderReading = e.recorderReading;
    if (isNonEmpty(e.cause)) effective.cause = e.cause;
    if (isNonEmpty(e.measures)) effective.measures = e.measures;
    if (isNonEmpty(e.result)) effective.result = e.result;
    if (isNonEmpty(e.employeeDetails)) effective.employeeDetails = e.employeeDetails;

    applyPhotos(e.photos);
  }

  effective.photos = Array.from(photoMap.values());
  const changedAt = maxDate(entriesAsc.map((e) => e.dateCreated || e.eventDate || e.repairDate));

  return { effective, entriesDesc: entriesAsc.slice().reverse(), changedAt };
};

// История значений конкретного поля: [новое -> старое]
// возвращаем { value, date } (date — когда добавили/обновили это значение)
const collectFieldHistory = (entriesDesc, pick, formatValue = (v) => v) => {
  const out = [];
  let lastKey = null;

  for (const e of entriesDesc || []) {
    const raw = pick(e);
    if (!isNonEmpty(raw)) continue;

    const key =
      typeof raw === "string"
        ? raw.trim()
        : typeof raw === "number"
        ? String(raw)
        : JSON.stringify(raw);

    if (key === lastKey) continue;
    lastKey = key;

    out.push({
      value: formatValue(raw),
      date: e?.dateCreated || e?.eventDate || e?.repairDate || null,
    });
  }

  return out;
};

const FieldWithHistory = ({ label, history, wide }) => {
  if (!history || history.length === 0) return null;

  const current = history[0];
  const previous = history.slice(1);

  if (!current || !isNonEmpty(current.value)) return null;

  return (
    <div
      className={
        "accident-row__field" + (wide ? " accident-row__field--wide" : "")
      }
    >
      <div className="accident-row__field-label">{label}</div>

      <div className="accident-row__field-value">
        {current.value}
      </div>

      {previous.length > 0 && (
        <div className="accident-row__history">
          <div className="accident-row__history-title">Маълумотлар тарихи</div>
          <ul className="accident-row__history-list">
            {previous.map((h, idx) => (
              <li key={idx} className="accident-row__history-item">
                <span className="accident-row__history-value">{h.value}</span>
                {h.date ? (
                  <span className="accident-row__history-date">
                    {" "}
                    — {formatDateTime(h.date)}
                  </span>
                ) : null}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

// -------------------- Normalization --------------------

const normalizeSubstationAccident = (acc, lastVisit) => {
  const eventDate =
    acc.event_date || acc.date || acc.event_datetime || acc.date_created;
  const dateCreated = acc.date_created || eventDate;

  const sub = acc.sub_accident || acc.substation || {};
  const city = sub.city || {};
  const repairDate = acc.repair_date || acc.repair_time || null;

  const employees = Array.isArray(acc.employees)
    ? acc.employees.map((e) => ({
        id: e.id,
        fullname: e.fullname,
        filial: e.filial,
        position: e.position,
      }))
    : [];

  const updates = Array.isArray(acc.updates)
    ? acc.updates.map((u) => normalizeUpdate(u, "substation"))
    : [];

  const changedAt = maxDate([dateCreated, ...updates.map((u) => u.dateCreated)]);
  const isNew = lastVisit && changedAt ? new Date(changedAt) > lastVisit : false;

  return {
    id: `sub-${acc.id}`,
    rawId: acc.id,
    raw: acc,

    type: "substation",
    typeLabel: "Подстанция",

    eventDate,
    repairDate,
    dateCreated,

    updates,
    changedAt,
    isNew,

    objectId: sub.id,
    objectName: sub.name || "—",
    filial: city.name || "",

    authorName: acc.author_user
      ? `${acc.author_user.lastname || ""} ${acc.author_user.name || ""}`.trim()
      : "",

    topic: acc.topic || "",
    protectionType: acc.protection_type || "",
    recorderReading: acc.recorder_reading || "",
    cause: acc.cause || "",
    measures: acc.measures || "",
    result: acc.result || "",
    employeeDetails: acc.employee_details || "",

    employees,

    photos: Array.isArray(acc.files)
      ? acc.files
          .filter((f) => f.directus_files_id)
          .map((f) => ({
            id: f.directus_files_id,
            url: `${DIRECTUS_URL}/assets/${f.directus_files_id}`,
          }))
      : [],
  };
};

const normalizeNetworkAccident = (acc, lastVisit) => {
  const eventDate =
    acc.event_date || acc.date || acc.event_datetime || acc.date_created;
  const dateCreated = acc.date_created || eventDate;

  const net = acc.network_id_accident_network || acc.network || {};
  const city = net.city || {};
  const repairDate = acc.repair_date || acc.repair_time || null;

  const employees = Array.isArray(acc.employees)
    ? acc.employees.map((e) => ({
        id: e.id,
        fullname: e.fullname,
        filial: e.filial,
        position: e.position,
      }))
    : [];

  const updates = Array.isArray(acc.updates)
    ? acc.updates.map((u) => normalizeUpdate(u, "network"))
    : [];

  const changedAt = maxDate([dateCreated, ...updates.map((u) => u.dateCreated)]);
  const isNew = lastVisit && changedAt ? new Date(changedAt) > lastVisit : false;

  return {
    id: `net-${acc.id}`,
    rawId: acc.id,
    raw: acc,

    type: "network",
    typeLabel: "Линия",

    eventDate,
    repairDate,
    dateCreated,

    updates,
    changedAt,
    isNew,

    objectId: net.id,
    objectName: net.name || "—",
    filial: city.name || "",

    authorName: acc.author_user
      ? `${acc.author_user.lastname || ""} ${acc.author_user.name || ""}`.trim()
      : "",

    topic: acc.topic || "",
    protectionType: acc.protection_type || "",
    recorderReading: acc.recorder_reading || "",
    cause: acc.cause || "",
    measures: acc.measures || "",
    result: acc.result || "",
    employeeDetails: acc.employee_details || "",

    employees,

    photos: Array.isArray(acc.files)
      ? acc.files
          .filter((f) => f.directus_files_id)
          .map((f) => ({
            id: f.directus_files_id,
            url: `${DIRECTUS_URL}/assets/${f.directus_files_id}`,
          }))
      : [],
  };
};

// -------------------- Page --------------------

const Accidents = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const refreshToken = location.state?.refreshToken;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [accidents, setAccidents] = useState([]);
  const [expandedId, setExpandedId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);

  const [photosOpen, setPhotosOpen] = useState(false);
  const [photosIndex, setPhotosIndex] = useState(0);
  const [photosItems, setPhotosItems] = useState([]); // для SchemeModal

  
  const openPhotosModal = (photos, startIndex) => {
    const items = (photos || []).map((p, i) => ({
      id: p.id,
      title: `Фото ${i + 1}`,
      thumb: p.url,
      full: p.url,
    }));

    setPhotosItems(items);
    setPhotosIndex(startIndex || 0);
    setPhotosOpen(true);
  };

  const [filters, setFilters] = useState({
    type: "all", // all | substation | network
    branch: "all",
    objectId: "all",
    employeeId: "all",
    from: "",
    to: "",
    search: "",
  });

  const handleLogout = () => {
    localStorage.removeItem("token");
    navigate("/login", { replace: true });
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError("");

        const lastVisitStr = localStorage.getItem("accidents_last_view") || null;
        const lastVisit = lastVisitStr ? new Date(lastVisitStr) : null;

        const [subRes, netRes] = await Promise.all([
          apiFetch("/api/substation-accidents", { cache: "no-store" }),
          apiFetch("/api/network-accidents", { cache: "no-store" }),
        ]);

        if (!subRes.ok || !netRes.ok) {
          throw new Error("Не удалось загрузить аварии");
        }

        const [subData, netData] = await Promise.all([
          subRes.json(),
          netRes.json(),
        ]);

        const normalized = [
          ...(Array.isArray(subData)
            ? subData.map((a) => normalizeSubstationAccident(a, lastVisit))
            : []),
          ...(Array.isArray(netData)
            ? netData.map((a) => normalizeNetworkAccident(a, lastVisit))
            : []),
        ].sort((a, b) => {
          const ad = new Date(a.changedAt || a.eventDate || a.dateCreated || 0).getTime();
          const bd = new Date(b.changedAt || b.eventDate || b.dateCreated || 0).getTime();
          return bd - ad;
        });

        setAccidents(normalized);

        localStorage.setItem("accidents_last_view", new Date().toISOString());
      } catch (e) {
        if (e?.status === 401) navigate("/login");
        else if (e?.status === 403) navigate("/no-access");
        else setError(e.message || "Ошибка загрузки");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [refreshToken, location.key, navigate]);

  const branchOptions = useMemo(() => {
    const set = new Set();
    accidents.forEach((a) => {
      if (a.filial) set.add(a.filial);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b, "ru"));
  }, [accidents]);

  const objectOptions = useMemo(() => {
    const map = new Map();
    accidents.forEach((a) => {
      if (!a.objectId || !a.objectName) return;
      const key = `${a.type}-${a.objectId}`;
      if (!map.has(key)) {
        map.set(key, {
          key,
          type: a.type,
          label: `${a.objectName} (${a.type === "substation" ? "ПС" : "Линия"})`,
        });
      }
    });
    return Array.from(map.values()).sort((a, b) =>
      a.label.localeCompare(b.label, "ru")
    );
  }, [accidents]);

  const employeeOptions = useMemo(() => {
    const map = new Map();
    accidents.forEach((a) => {
      (a.employees || []).forEach((emp) => {
        if (!map.has(emp.id)) map.set(emp.id, emp);
      });
    });
    return Array.from(map.values()).sort((a, b) =>
      a.fullname.localeCompare(b.fullname, "ru")
    );
  }, [accidents]);

  const filteredAccidents = useMemo(() => {
    const fromDate = filters.from ? new Date(filters.from) : null;
    const toDate = filters.to ? new Date(filters.to + "T23:59:59") : null;
    const search = filters.search?.trim();

    return accidents.filter((a) => {
      if (filters.type !== "all" && a.type !== filters.type) return false;

      if (filters.branch !== "all" && a.filial !== filters.branch) return false;

      if (filters.objectId !== "all") {
        const objKey = `${a.type}-${a.objectId}`;
        if (objKey !== filters.objectId) return false;
      }

      if (filters.employeeId !== "all") {
        const hasEmployee = (a.employees || []).some(
          (emp) => String(emp.id) === filters.employeeId
        );
        if (!hasEmployee) return false;
      }

      const evDate = a.eventDate ? new Date(a.eventDate) : null;
      if (fromDate && evDate && evDate < fromDate) return false;
      if (toDate && evDate && evDate > toDate) return false;

      if (search) {
        const employeesText = (a.employees || []).map((e) => e.fullname).join(" ");
        const haystack = normalize([
          a.objectName,
          a.topic,
          a.cause,
          a.measures,
          a.filial,
          employeesText,
          a.employeeDetails,
        ].filter(Boolean).join(" "));
        if (!fuzzyIncludes(haystack, search)) return false;
      }

      return true;
    });
  }, [accidents, filters]);

  const totalPages = Math.max(1, Math.ceil(filteredAccidents.length / ACCIDENTS_PAGE_SIZE));

  useEffect(() => {
    setCurrentPage((prev) => Math.min(Math.max(prev, 1), totalPages));
  }, [totalPages]);

  const paginatedAccidents = useMemo(() => {
    const start = (currentPage - 1) * ACCIDENTS_PAGE_SIZE;
    return filteredAccidents.slice(start, start + ACCIDENTS_PAGE_SIZE);
  }, [currentPage, filteredAccidents]);

  const pageStart = filteredAccidents.length
    ? (currentPage - 1) * ACCIDENTS_PAGE_SIZE + 1
    : 0;
  const pageEnd = Math.min(currentPage * ACCIDENTS_PAGE_SIZE, filteredAccidents.length);

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value,
      ...(field === "type" ? { objectId: "all" } : {}),
    }));
    setCurrentPage(1);
    setExpandedId(null);
  };

  const resetFilters = () => {
    setFilters({
      type: "all",
      branch: "all",
      objectId: "all",
      employeeId: "all",
      from: "",
      to: "",
      search: "",
    });
    setCurrentPage(1);
    setExpandedId(null);
  };

  const toggleExpand = (id) => {
    setExpandedId((prev) => (prev === id ? null : id));
  };

  const summary = useMemo(() => {
    const updateTotal = accidents.reduce((sum, acc) => sum + (acc.updates?.length || 0), 0);
    const photoTotal = accidents.reduce((sum, acc) => {
      const updatePhotos = (acc.updates || []).reduce(
        (inner, update) => inner + (update.photos?.length || 0),
        0
      );
      return sum + (acc.photos?.length || 0) + updatePhotos;
    }, 0);

    return {
      total: accidents.length,
      filtered: filteredAccidents.length,
      substations: accidents.filter((acc) => acc.type === "substation").length,
      networks: accidents.filter((acc) => acc.type === "network").length,
      fresh: accidents.filter((acc) => acc.isNew).length,
      updates: updateTotal,
      photos: photoTotal,
    };
  }, [accidents, filteredAccidents.length]);

  return (
    <Layout>
      <div className="container">
        <div className="accidents-page">
          <section className="accidents-hero">
            <div className="accidents-hero__content">
              <span className="accidents-kicker">Оператив журнал</span>
              <h1 className="accidents-page__title">Ўчишлар</h1>
              <p className="accidents-page__subtitle">
                Подстанция ва линиялардаги ўчишлар, қўшимча маълумотлар, фото ва
                масъул ходимлар бўйича ягона реестр.
              </p>
            </div>

            <div className="accidents-page__actions">
              <Link to="/accidents/add" className="accidents-button accidents-button--primary">
                Ўчишни қўшиш
              </Link>
              <button
                type="button"
                className="accidents-button accidents-button--ghost"
                onClick={handleLogout}
              >
                Чиқиш
              </button>
            </div>

            <div className="accidents-metrics">
              <div className="accidents-metric">
                <span>Жами</span>
                <strong>{summary.total}</strong>
              </div>
              <div className="accidents-metric">
                <span>Фильтрда</span>
                <strong>{summary.filtered}</strong>
              </div>
              <div className="accidents-metric">
                <span>Подстанция</span>
                <strong>{summary.substations}</strong>
              </div>
              <div className="accidents-metric">
                <span>Линия</span>
                <strong>{summary.networks}</strong>
              </div>
            </div>
          </section>

          <section className="accidents-filters accidents-panel">
            <div className="accidents-panel__head">
              <div>
                <h2>Фильтрлар</h2>
                <p>Филиал, объект, сана ва матн бўйича тез саралаш.</p>
              </div>
              <div className="accidents-panel__badges">
                {summary.fresh > 0 && <span className="accidents-status-chip">NEW {summary.fresh}</span>}
                <span className="accidents-status-chip">{summary.updates} update</span>
                <span className="accidents-status-chip">{summary.photos} фото</span>
              </div>
            </div>

            <div className="accidents-filters__grid">
              <div className="accidents-filter-group accidents-filter-group--type">
                <span className="accidents-filter-group__label">Объект</span>
                <div className="accidents-filter-chips">
                  <button
                    type="button"
                    className={
                      "accidents-chip" +
                      (filters.type === "all" ? " accidents-chip--active" : "")
                    }
                    onClick={() => handleFilterChange("type", "all")}
                  >
                    Барчаси
                  </button>
                  <button
                    type="button"
                    className={
                      "accidents-chip" +
                      (filters.type === "substation"
                        ? " accidents-chip--active"
                        : "")
                    }
                    onClick={() => handleFilterChange("type", "substation")}
                  >
                    Подстанциялар
                  </button>
                  <button
                    type="button"
                    className={
                      "accidents-chip" +
                      (filters.type === "network"
                        ? " accidents-chip--active"
                        : "")
                    }
                    onClick={() => handleFilterChange("type", "network")}
                  >
                    Линии
                  </button>
                </div>
              </div>

              <div className="accidents-filter-group accidents-filter-group--branch">
                <label className="accidents-filter-group__label">Филиал</label>
                <select
                  className="accidents-select"
                  value={filters.branch}
                  onChange={(e) => handleFilterChange("branch", e.target.value)}
                >
                  <option value="all">Барча филиаллар</option>
                  {branchOptions.map((b) => (
                    <option key={b} value={b}>
                      {b}
                    </option>
                  ))}
                </select>
              </div>

              <div className="accidents-filter-group accidents-filter-group--object">
                <label className="accidents-filter-group__label">Объект (номи)</label>
                <select
                  className="accidents-select"
                  value={filters.objectId}
                  onChange={(e) => handleFilterChange("objectId", e.target.value)}
                >
                  <option value="all">Барча объектлар</option>
                  {objectOptions
                    .filter((o) => filters.type === "all" || o.type === filters.type)
                    .map((o) => (
                      <option key={o.key} value={o.key}>
                        {o.label}
                      </option>
                    ))}
                </select>
              </div>

              <div className="accidents-filter-group accidents-filter-group--date">
                <label className="accidents-filter-group__label">Санадан</label>
                <input
                  type="date"
                  className="accidents-input"
                  value={filters.from}
                  onChange={(e) => handleFilterChange("from", e.target.value)}
                />
              </div>

              <div className="accidents-filter-group accidents-filter-group--date">
                <label className="accidents-filter-group__label">Санагача</label>
                <input
                  type="date"
                  className="accidents-input"
                  value={filters.to}
                  onChange={(e) => handleFilterChange("to", e.target.value)}
                />
              </div>

              <div className="accidents-filter-group accidents-filter-group--employee">
                <label className="accidents-filter-group__label">Ходим</label>
                <select
                  className="accidents-select"
                  value={filters.employeeId}
                  onChange={(e) => handleFilterChange("employeeId", e.target.value)}
                >
                  <option value="all">Барча ходимлар</option>
                  {employeeOptions.map((emp) => (
                    <option key={emp.id} value={emp.id}>
                      {emp.fullname}
                      {emp.filial ? ` (${emp.filial})` : ""}
                    </option>
                  ))}
                </select>
              </div>

              <div className="accidents-filter-group accidents-filter-group--search">
                <label className="accidents-filter-group__label">
                  Қидириш (мавзу, сабаб, объект, филиал, ходим)
                </label>
                <input
                  type="text"
                  className="accidents-input"
                  placeholder="Матн бўйича қидириш..."
                  value={filters.search}
                  onChange={(e) => handleFilterChange("search", e.target.value)}
                />
              </div>

              <div className="accidents-filter-actions">
                <button
                  type="button"
                  className="accidents-button accidents-button--ghost"
                  onClick={resetFilters}
                >
                  Тозалаш
                </button>
              </div>
            </div>
          </section>

          <section className="accidents-list-section">
            <div className="accidents-list-section__head">
              <div>
                <h2>Реестр</h2>
                <p>
                  {summary.filtered} та ёзув топилди. {pageStart}-{pageEnd} кўрсатилмоқда.
                </p>
              </div>
              {totalPages > 1 && (
                <div className="accidents-pagination accidents-pagination--top">
                  <button
                    type="button"
                    className="accidents-page-btn"
                    disabled={currentPage <= 1}
                    onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
                  >
                    Олдинги
                  </button>
                  <span>
                    {currentPage} / {totalPages}
                  </span>
                  <button
                    type="button"
                    className="accidents-page-btn"
                    disabled={currentPage >= totalPages}
                    onClick={() => setCurrentPage((page) => Math.min(totalPages, page + 1))}
                  >
                    Кейинги
                  </button>
                </div>
              )}
            </div>

            {loading && <div className="accidents-state">Юкланмоқда...</div>}

            {error && !loading && (
              <div className="accidents-state accidents-state--error">{error}</div>
            )}

            {!loading && !error && filteredAccidents.length === 0 && (
              <div className="accidents-state">
                Танланган фильтрлар бўйича ўчиш топилмади.
              </div>
            )}

            {!loading && !error && filteredAccidents.length > 0 && (
              <div className="accidents-list">
                {paginatedAccidents.map((acc) => {
                  const isExpanded = expandedId === acc.id;

                  const { effective: view, entriesDesc } = buildAccidentTimeline(acc);

                  // const lastAuthor =
                  //   entriesDesc?.[0]?.authorName || view.authorName || "";

                  // const hAuthor = collectFieldHistory(entriesDesc, (e) => e.authorName);
                  const creatorName = (acc.authorName || "").trim();

                  const contributors = Array.from(
                    new Set(
                      (acc.updates || [])
                        .map((u) => (u.authorName || "").trim())
                        .filter((n) => isNonEmpty(n))
                    )
                  ).filter((n) => n !== creatorName);

                  const hRepair = collectFieldHistory(
                    entriesDesc,
                    (e) => e.repairDate,
                    (v) => formatDate(v)
                  );
                  const hRecorder = collectFieldHistory(entriesDesc, (e) => e.recorderReading);
                  const hTopic = collectFieldHistory(entriesDesc, (e) => e.topic);
                  const hProtection = collectFieldHistory(entriesDesc, (e) => e.protectionType);
                  const hCause = collectFieldHistory(entriesDesc, (e) => e.cause);
                  const hMeasures = collectFieldHistory(entriesDesc, (e) => e.measures);
                  const hResult = collectFieldHistory(entriesDesc, (e) => e.result);
                  const hEmployeeDetails = collectFieldHistory(entriesDesc, (e) => e.employeeDetails);

                  return (
                    <article
                      key={acc.id}
                      className={
                        "accident-row" +
                        (acc.isNew ? " accident-row--new" : "") +
                        (isExpanded ? " accident-row--expanded" : "")
                      }
                    >
                      <button
                        type="button"
                        className="accident-row__header"
                        onClick={() => toggleExpand(acc.id)}
                      >
                        <div className="accident-row__header-main">
                          <div className="accident-row__title-row">
                            <span className="accident-row__type-pill">{acc.typeLabel}</span>
                            {acc.isNew && <span className="accident-row__badge-new">NEW</span>}
                            {(acc.updates?.length || 0) > 0 && (
                              <span className="accident-row__update-pill">
                                {acc.updates.length} update
                              </span>
                            )}
                          </div>

                          <span className="accident-row__object-name">{acc.objectName}</span>

                          <div className="accident-row__meta">
                            {acc.filial && (
                              <span className="accident-row__meta-item">{acc.filial}</span>
                            )}
                            {view.topic && (
                              <span
                                className="accident-row__meta-item accident-row__meta-item--topic"
                                title={view.topic}
                              >
                                {view.topic}
                              </span>
                            )}
                          </div>
                        </div>

                        <div className="accident-row__header-right">
                          {view.photos && view.photos.length > 0 && (
                            <div className="accident-row__photos-count">
                              {view.photos.length} фото
                            </div>
                          )}

                          <div className="accident-row__date">
                            {formatDate(view.eventDate || acc.eventDate)}
                          </div>
                          <div className="accident-row__chevron">{isExpanded ? "▲" : "▼"}</div>
                        </div>
                      </button>

                      {isExpanded && (
                        <div className="accident-row__body">
                          <div className="accident-row__body-grid">
                            {(isNonEmpty(creatorName) || contributors.length > 0) && (
                              <div className="accident-row__field">
                                <div className="accident-row__field-label">Ходим</div>

                                <div className="accident-row__field-value">
                                  {creatorName}

                                  {contributors.length > 0 && (
                                    <div className="accident-row__field-subvalue">
                                      Маълумот қўшган ходимлар: {contributors.join(", ")}
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}

                            <FieldWithHistory
                              label="Ремонт вақти"
                              value={isNonEmpty(view.repairDate) ? formatDate(view.repairDate) : ""}
                              history={hRepair}
                            />

                            {acc.type === "network" && (
                              <FieldWithHistory
                                label="Қайд этувчи мослама кўрсаткичи"
                                value={view.recorderReading}
                                history={hRecorder}
                              />
                            )}

                            <FieldWithHistory
                              label="Ўчиш тури"
                              value={view.topic}
                              history={hTopic}
                              wide
                            />

                            <FieldWithHistory
                              label="Ишлаган ҳимоя тури"
                              value={view.protectionType}
                              history={hProtection}
                              wide
                            />

                            <FieldWithHistory
                              label="Аниқланган сабаби"
                              value={view.cause}
                              history={hCause}
                              wide
                            />

                            <FieldWithHistory
                              label="Кўрилган чоралар"
                              value={view.measures}
                              history={hMeasures}
                              wide
                            />

                            {acc.type === "network" && (
                              <FieldWithHistory
                                label="АҚУ натижаси"
                                value={view.result}
                                history={hResult}
                                wide
                              />
                            )}
                          </div>

                          {view.photos && view.photos.length > 0 && (
                            <div className="accident-row__photos">
                              {view.photos.map((photo, idx) => (
                                <button
                                  key={photo.id}
                                  type="button"
                                  className="accident-photo"
                                  onClick={() => openPhotosModal(view.photos, idx)}
                                  title={`Фото ${idx + 1}`}
                                >
                                  <img
                                    src={photo.url}
                                    alt=""
                                    className="accident-photo__img"
                                    loading="lazy"
                                  />
                                </button>
                              ))}
                            </div>
                          )}

                          {/* айбдор ходимлар (один раз, без дубля) */}
                          {acc.employees && acc.employees.length > 0 && (
                            <div className="accident-row__employees">
                              <div className="accident-row__employees-header">
                                Айбдор ходимлар
                              </div>
                              <ul className="accident-row__employees-list">
                                {acc.employees.map((emp) => (
                                  <li key={emp.id} className="accident-row__employee">
                                    <div className="accident-row__employee-name">
                                      {emp.fullname}
                                    </div>
                                    {(emp.position || emp.filial) && (
                                      <div className="accident-row__employee-meta">
                                        {emp.position && <span>{emp.position}</span>}
                                        {emp.position && emp.filial && <span> · </span>}
                                        {emp.filial && <span>{emp.filial}</span>}
                                      </div>
                                    )}
                                  </li>
                                ))}
                              </ul>
                              <FieldWithHistory
                                label="Ходимлар бўйича маълумот"
                                history={hEmployeeDetails}
                                wide
                              />
                            </div>
                          )}
                          <div className="accident-row__actions">
                            <button
                              type="button"
                              className="accidents-button accidents-button--primary"
                              onClick={() =>
                                navigate(`/accidents/${acc.type}/${acc.rawId}/add-info`, {
                                  state: { accident: acc.raw },
                                })
                              }
                            >
                              Малумот қўшиш
                            </button>
                          </div>
                        </div>
                      )}
                    </article>
                  );
                })}
              </div>
            )}

            {!loading && !error && totalPages > 1 && (
              <div className="accidents-pagination">
                <button
                  type="button"
                  className="accidents-page-btn"
                  disabled={currentPage <= 1}
                  onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
                >
                  Олдинги
                </button>
                <span>
                  {pageStart}-{pageEnd} / {summary.filtered}
                </span>
                <button
                  type="button"
                  className="accidents-page-btn"
                  disabled={currentPage >= totalPages}
                  onClick={() => setCurrentPage((page) => Math.min(totalPages, page + 1))}
                >
                  Кейинги
                </button>
              </div>
            )}
          </section>
        </div>
      </div>
      <SchemeModal
        open={photosOpen}
        items={photosItems}
        index={photosIndex}
        onClose={() => {
          setPhotosOpen(false);
          setPhotosItems([]);
          setPhotosIndex(0);
        }}
        onChangeIndex={setPhotosIndex}
      />
    </Layout>
  );
};

export default Accidents;
